/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { FunctionComponent } from "react";

/**
 * Content sizes
 */
export enum CONTENT_SIZE {
	SMALL = "small", // ~40%
	MEDIUM = "medium", // ~60%
	LARGE = "large", // ~75%
	FULL = "full" // ~90%
}

export enum CONTENT_ALIGN_VR {
	TOP = "top",
	MIDDLE = "middle",
	BOTTOM = "bottom",
	STRETCH = "stretch"
}

export enum CONTENT_PADDING_VR {
	NONE = "none",
	SMALL = "small",
	MEDIUM = "medium",
	LARGE = "large"
}

/**
 * Component props
 */
export interface ICenterContentProps {
	size: CONTENT_SIZE;
	verticalAlign?: CONTENT_ALIGN_VR;
	verticalPadding?: CONTENT_PADDING_VR;
	className?: string;
}

/**
 * Center content component
 */
export const CenterContent: FunctionComponent<ICenterContentProps> = ({
	size,
	verticalPadding,
	verticalAlign,
	children,
	className
}) => {
	const baseClass = BEM("edt-center-content");
	baseClass.mod("size-" + size);
	baseClass.mod("padding-vr-" + (verticalPadding || CONTENT_PADDING_VR.NONE));
	baseClass.mod("align-vr-" + (verticalAlign || CONTENT_ALIGN_VR.MIDDLE));

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("content").toClassName()}>{children}</div>
		</div>
	);
};
